/*
 * Ryan O'Dowd
 * 2025-10-18
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import IconButton from '@components/common/IconButton';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  adminDeleteBatch,
} from '@utils/store/actions';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const PendingBatchesTable = (props: PendingBatchesTableProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const [_batchIdToDelete, setBatchIdToDelete] = useState(null);

  const tableRows = props.batches.map((batch) => {
    return [
      {
        id: `${batch.batch_id}_time`,
        content: <Tooltip title={dateFnsFormat(new Date(batch.synced_at), 'h:mm a')}><Text>{dateFnsFormat(new Date(batch.synced_at), 'MMM d, yyyy')}</Text></Tooltip>,
        sortValue: batch.synced_at, // @TODO: make this a datetime sort
      },
      {
        id: `${batch.batch_id}_translation`,
        content: <Tooltip title={batch.translation_name}><Text>{batch.translation_abbreviation}</Text></Tooltip>,
        sortValue: batch.translation_abbreviation.toLocaleLowerCase(),
      },
      {
        id: `${batch.batch_id}_num_changes`,
        content: <Text>{batch.num_staged_changes}/{batch.num_total_changes}</Text>,
        sortValue: +batch.num_staged_changes,
      },
      {
        id: `${batch.batch_id}_batch_id`,
        content: <Text>{batch.batch_id}</Text>,
        sortValue: batch.batch_id,
      },
      {
        id: `${batch.batch_id}_delete`,
        content: (
          <IconButton
            icon='delete'
            iconColor={styles.colors.alert}
            onPress={() => setBatchIdToDelete(batch.batch_id)}
          />
        ),
        sortValue: 0, // @TODO: really doesn't need to be sortable
      },
    ];
  });

  return (
    <React.Fragment>
      <Table
        tableTitle='Pending/partial batches'
        open={false}
        hasBadge={true}
        badgeText={`${props.batches.length}`}
        headerRowData={[
          {width: 140, id: 'batch_started_at', content: 'Sync started at'},
          {width: 90, id: 'translation', content: 'Translation'},
          {width: 90, id: 'num_changes', content: '# changes (staged/total)'},
          {width: 160, id: 'batch_id', content: 'Batch ID', rightAlign: true},
          {width: 90, id: 'delete', content: 'Delete?', rightAlign: true},
        ]}
        isSortable={true}
        orderByIndex={0}
        rowData={tableRows || []}// @TODO: shouldn't be clickable (or if they are, make individually updatable)// @TODO: shouldn't need || []
      />
      <Dialog
        style={styles.container}
        visible={_batchIdToDelete !== null}
        onDismiss={() => setBatchIdToDelete(null)}
      >
        <Dialog.Title>Edit Bible</Dialog.Title>
        <Dialog.Content>
          <Text>This will hard delete all records in api_sync_staging_metadata and any staging tables (i.e. translations_staging, books_staging, footnotes_staging, verse_texts_staging)</Text>
          <Text>There is a back-end check to make sure that no batches that have accepted/rejected records (even partially) will be deleted.</Text>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='text'
            onPress={() => setBatchIdToDelete(null)}
          >
            {i18n('cancel')}
          </Button>
          <Button
            theme={styles.colors.alert}
            onPress={() => {
              /* @TODO: make sure there's a back-end check which guarantees no deletion of partially accepted batches */
              dispatch(adminDeleteBatch(_batchIdToDelete, props.fetchApiSyncStagingMetadataSuccessCallback));
              setBatchIdToDelete(null);
            }}
          >
            {i18n('delete')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </React.Fragment>
  );
};

class PendingBatchesTableProps {
  batches: any;
  fetchApiSyncStagingMetadataSuccessCallback: () => void;
};

export default PendingBatchesTable;
